import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { Seo, breadcrumbJsonLd, faqJsonLd, SITE_URL } from "../../components/Seo";
import BusinessHeader from "../../components/business/BusinessHeader";
import BusinessFooter from "../../components/business/BusinessFooter";
import { DbArticleList } from "../../components/viden/DbArticleList";
import BusinessLeadForm from "../../components/business/BusinessLeadForm";
import { Blocks, FaqBlock, CtaBlock, WRAP } from "../../components/business/blocks";
import { GOLD, NAVY, Aurora, GoldLink, OutlineLink } from "../../components/business/ui";
import { BUSINESS_PAGES } from "../../data/business/pages";
import { businessServiceJsonLd } from "../../data/business/profile";
import { knowledgeCollectionJsonLd } from "../../data/business/knowledge";
import NotFoundPage from "../NotFoundPage";

const BusinessSubPage = () => {
  const { slug } = useParams();
  const page = BUSINESS_PAGES[slug];

  // ScrollToTop håndterer #ankre, så her scrolles kun når der ikke er et.
  useEffect(() => { if (!window.location.hash) window.scrollTo(0, 0); }, [slug]);

  if (!page) return <NotFoundPage />;

  const path = `/business/${page.slug}`;
  // Svarboksens H2 er et spørgsmål med et direkte svar lige under. Den tages med i
  // FAQPage-schemaet, så AI-søgemaskiner kan citere svaret uden at fortolke resten af siden.
  const answerQa = (page.blocks || [])
    .filter((b) => b.type === "answer" && b.heading && b.paragraphs?.length)
    .map((b) => ({ q: b.heading, a: b.paragraphs.join(" ") }));
  const schemaFaq = [...answerQa, ...(page.faq || [])];
  const crumbs = [
    { name: "Forside", path: "/" },
    { name: "Votra Business", path: "/business" },
    ...(page.parentNav ? [{ name: page.parentNav.name, path: page.parentNav.path }] : []),
    { name: page.h1, path },
  ];
  const jsonLd = [
    breadcrumbJsonLd(crumbs),
    ...(page.slug === "viden" ? [knowledgeCollectionJsonLd(SITE_URL, path)] : []),
    ...(page.service ? [businessServiceJsonLd({ ...page.service, path })] : []),
    ...(schemaFaq.length ? [faqJsonLd(schemaFaq, `${SITE_URL}${path}#faq`)] : []),
  ];

  return (
    <div className="votra-biz min-h-screen flex flex-col bg-white" data-testid={`business-page-${page.slug}`}>
      <Seo title={page.meta.title} description={page.meta.description} path={path} jsonLd={jsonLd} noindex={!!page.noindex} brand="business" />
      <div className="votra-grain" aria-hidden />
      <BusinessHeader />

      {/* Hero */}
      <main id="indhold">
      <section className="relative overflow-hidden" style={{ backgroundColor: NAVY }} data-testid="subpage-hero">
        <Aurora grid />
        <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ backgroundColor: GOLD }} />
        <div className={`${WRAP} relative py-16 md:py-24`}>
          <nav className="flex items-center flex-wrap gap-1.5 text-xs text-slate-400 mb-8" aria-label="Brødkrumme">
            {crumbs.slice(0, -1).map((c) => (
              <span key={c.path} className="flex items-center gap-1.5">
                <Link to={c.path} className="hover:text-slate-300 transition-colors">{c.name}</Link>
                <ChevronRight className="h-3 w-3" />
              </span>
            ))}
            <span className="text-slate-400">{page.eyebrow}</span>
          </nav>

          <div className="max-w-4xl">
            <p className="votra-hero-rise votra-hero-rise--1 text-xs font-semibold uppercase tracking-[0.3em] mb-6" style={{ color: GOLD }}>
              {page.eyebrow}
            </p>
            <h1 className="votra-hero-rise votra-hero-rise--2 font-display text-white font-medium leading-[1.08] text-4xl sm:text-5xl lg:text-[3.5rem] mb-7">
              {page.h1}
            </h1>
            <p className="votra-hero-rise votra-hero-rise--3 text-slate-300 text-base md:text-lg leading-relaxed max-w-3xl">
              {page.lead}
            </p>
            <div className="votra-hero-rise votra-hero-rise--4 flex flex-col sm:flex-row gap-4 mt-10">
              <GoldLink to="/business/kontakt" testid="subpage-primary-cta">
                Få en driftsgennemgang
              </GoldLink>
              <OutlineLink light to={page.parentNav?.path || "/business/vognmaend"} testid="subpage-secondary-cta">
                {page.parentNav ? `Tilbage til ${page.parentNav.name.toLowerCase()}` : "Se hvad vi overtager"}
              </OutlineLink>
            </div>
          </div>
        </div>
      </section>

      <Blocks blocks={page.blocks} onContact={<BusinessLeadForm />} />
      <FaqBlock items={page.faq} />
      {page.cta && <CtaBlock title={page.cta.title} button={page.cta.button} to={page.cta.to} />}
      </main>

      {page.slug === "viden" && <DbArticleList company="business" />}
      <BusinessFooter />
    </div>
  );
};

export default BusinessSubPage;
